import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    Row, Col, Card, Spin, Alert, Radio, Space, DatePicker, Select, Tooltip, Tag,
    Statistic, Progress, Empty,
} from 'antd';
import { Grid, theme } from 'antd';
import {
    ShoppingCartOutlined, QuestionCircleOutlined, LineChartOutlined, BarChartOutlined,
    LockOutlined, BankOutlined, ThunderboltOutlined, InfoCircleOutlined,
    ArrowUpOutlined, ArrowDownOutlined, MinusOutlined, ReloadOutlined, InboxOutlined, PhoneOutlined,
} from '@ant-design/icons';
import {
    LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
    ResponsiveContainer, Cell, LabelList,
} from 'recharts';
import dayjs from 'dayjs';
import { dashboardApi } from '../../api/client';
import { useAuthStore } from '../../store/auth.store';
import { formatValue } from '../../utils/chartFormatters';

const { useBreakpoint } = Grid;
const { RangePicker } = DatePicker;

type ChannelType = 'ALLEGRO' | 'SHOP' | 'WHOLESALE';
type Mode = 'MTD' | 'CUSTOM';

interface V2KPI { value: number; previousValue: number; change: number; trend: 'up' | 'down' | 'neutral' }
interface V2Data {
    generatedAt: string;
    period: { start: string; end: string };
    kpi: {
        orders: V2KPI; revenue: V2KPI; salesProfit: V2KPI; margin: V2KPI;
        allegroFees: V2KPI; operatingCosts: V2KPI; operatingResult: V2KPI;
    };
    returns: { count: number; amount: number; pctOfRevenue: number };
    waterfall: { label: string; value: number; kind: 'revenue' | 'cost' | 'result'; tooltip: string }[];
    costStructure: { category: string; amount: number; percentage: number }[];
    sales: {
        daily: { date: string; revenue: number; orders: number; profit: number }[];
        products: { name: string; value: number }[];
        trend: { change: number; avgCurrent: number; avgPrev: number } | null;
    };
    channels: { label: string; orders: number; revenue: number; profit: number; margin: number; share: number }[];
    b2b: {
        totalOrders: number; totalRevenue: number; margin: number;
        contractors: { name: string; orders: number; revenue: number; margin: number }[];
        available: boolean;
    };
    warehouse: {
        totalUnits: number;
        totalProducts: number;
        locations: { name: string; products: number; units: number }[];
        lowestStock: {
            id: string; name: string; sku: string | null; units: number;
            minStockLevel: number | null; split: { name: string; units: number }[];
        }[];
    };
    reviews: {
        pending: number;
        contactedToday: number;
        contactedYesterday: number;
        contactedThisWeek: number;
        contactedThisMonth: number;
    };
    operations: {
        toShip: number; slaBreaches: number; oldestUnshippedHours: number | null; avgProcessingHours: number;
        lowStock: { total: number; critical: number; top: { name: string; deficit: number } | null };
        fefo: { batches: number; value: number };
        alerts: { severity: 'error' | 'warning' | 'info'; text: string; link?: string }[];
    };
}

const fmtPLN = (v: number, frac = 2) =>
    `${(Number(v) || 0).toLocaleString('pl-PL', { minimumFractionDigits: frac, maximumFractionDigits: frac })} zł`;

const fmtPct = (v: number) => `${(Number(v) || 0).toFixed(1)}%`;

const CHANGE_COLORS = (change: number) => (change > 0 ? '#52c41a' : change < 0 ? '#ff4d4f' : '#8c8c8c');

/** Human-friendly duration for SLA display: >=48h renders as days */
const fmtDuration = (hours: number) => (hours >= 48 ? `${Math.round(hours / 24)} dni` : `${Math.round(hours)} h`);

// ===== Lifecycle helpers =====

const PRESETS = [
    { key: 'TODAY', label: 'Dziś' },
    { key: '7D', label: '7 dni' },
    { key: 'MTD', label: 'Ten miesiąc' },
    { key: 'QTD', label: 'Ten kwartał' },
    { key: 'YTD', label: 'Ten rok' },
    { key: '12M', label: '12 miesięcy' },
    { key: 'CUSTOM', label: 'Własny' },
] as const;
type PresetKey = typeof PRESETS[number]['key'];

function resolveRange(preset: PresetKey, custom: [dayjs.Dayjs, dayjs.Dayjs] | null): [dayjs.Dayjs, dayjs.Dayjs] {
    const now = dayjs();
    switch (preset) {
        case 'TODAY': return [now.startOf('day'), now];
        case '7D': return [now.subtract(6, 'day').startOf('day'), now];
        case 'MTD': return [now.startOf('month'), now];
        case 'QTD': {
            const month = Math.floor(now.month() / 3) * 3;
            return [now.month(month).startOf('month'), now];
        }
        case 'YTD': return [now.startOf('year'), now];
        case '12M': return [now.subtract(11, 'month').startOf('month'), now];
        case 'CUSTOM': return custom || [now.startOf('month'), now];
    }
}

// ===== Small building blocks =====

function KPICard({
    label, tooltip, value, suffix, color, footnote,
}: {
    label: string; tooltip?: React.ReactNode; value: string; suffix?: string; color?: string; footnote?: React.ReactNode;
}) {
    return (
        <Card size="small" style={{ height: '100%' }}>
            {tooltip ? (
                <Tooltip title={tooltip}>
                    <div style={{ color: '#666', fontSize: 12, marginBottom: 4, cursor: 'help' }}>
                        {label} <QuestionCircleOutlined style={{ fontSize: 10 }} />
                    </div>
                </Tooltip>
            ) : (
                <div style={{ color: '#666', fontSize: 12, marginBottom: 4 }}>{label}</div>
            )}
            <div style={{ fontSize: 20, fontWeight: 600, color: color || undefined, whiteSpace: 'nowrap' }}>
                {value} {suffix && <span style={{ fontSize: 14, fontWeight: 400 }}>{suffix}</span>}
            </div>
            {footnote && <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>{footnote}</div>}
        </Card>
    );
}

function ChangeTag({ change, absolute }: { change: number; absolute?: boolean }) {
    if (!change) return <span style={{ fontSize: 11, color: '#999' }}><MinusOutlined /> 0{absolute ? ' p.p.' : '%'}</span>;
    const up = change > 0;
    return (
        <span style={{ fontSize: 11, color: CHANGE_COLORS(change) }}>
            {up ? <ArrowUpOutlined /> : <ArrowDownOutlined />} {up ? '+' : ''}{change}
            {absolute ? ' p.p.' : '%'}
        </span>
    );
}

function LockedTag() {
    return (
        <Tooltip title="Sekcja wymaga dodatkowych uprawnień">
            <Tag icon={<LockOutlined />} style={{ fontSize: 11, marginLeft: 'auto' }}>uprawnienia</Tag>
        </Tooltip>
    );
}

function SectionTitle({ icon, title, extra }: { icon: React.ReactNode; title: string; extra?: React.ReactNode }) {
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '8px 0 -6px' }}>
            {icon}
            <span style={{ fontWeight: 600, fontSize: 15 }}>{title}</span>
            {extra}
        </div>
    );
}

// ===== Main component =====

export const CompanyDashboard = () => {
    const { token } = theme.useToken();
    const screens = useBreakpoint();
    const isMobile = !screens.md;
    const hasPermission = useAuthStore((s) => s.hasPermission);

    const canCosts = hasPermission('costs:read');
    const canInventory = hasPermission('inventory:read');
    const canPartners = hasPermission('partners:read');

    const [preset, setPreset] = useState<PresetKey>('MTD');
    const [customRange, setCustomRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
    const [channelType, setChannelType] = useState<ChannelType | undefined>(undefined);

    const range = resolveRange(preset, customRange);

    const { data, isLoading, isError, refetch, dataUpdatedAt } = useQuery({
        queryKey: ['dashboard-v2', range[0].format('YYYY-MM-DD'), range[1].format('YYYY-MM-DD'), channelType || 'all'],
        queryFn: () =>
            dashboardApi
                .getV2(
                    'CUSTOM',
                    range[0].format('YYYY-MM-DD'),
                    range[1].format('YYYY-MM-DD'),
                    channelType,
                )
                .then((res: any) => res.data as V2Data),
    });

    const waterfallChart = useMemo(() => {
        if (!data) return [];
        let cumulative = 0;
        return data.waterfall.map((row) => {
            if (row.kind === 'revenue') {
                cumulative = row.value;
                return { name: row.label, base: 0, value: row.value, kind: row.kind, tooltip: row.tooltip };
            }
            if (row.kind === 'cost') {
                // value is negative - floating bar between the running totals
                const base = cumulative + row.value;
                const entry = { name: row.label, base, value: Math.abs(row.value), kind: row.kind, tooltip: row.tooltip };
                cumulative += row.value;
                return entry;
            }
            // result (Zysk ze sprzedaży / Wynik operacyjny): bar grounded at zero
            const base = Math.min(0, row.value);
            return { name: row.label, base, value: Math.abs(row.value), kind: row.kind, tooltip: row.tooltip };
        });
    }, [data]);

    const colorFor = (kind: string) =>
        kind === 'revenue' ? '#1890ff' : kind === 'cost' ? '#ff7875' : '#52c41a';

    if (isLoading) {
        return <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>;
    }
    if (isError || !data) {
        return (
            <Alert
                message="Błąd ładowania danych dashboardu"
                action={<a onClick={() => refetch()}>Spróbuj ponownie</a>}
                type="error"
            />
        );
    }

    const { kpi } = data;
    const marginColor = kpi.margin.value >= 20 ? '#52c41a' : kpi.margin.value >= 10 ? '#faad14' : '#ff4d4f';
    const resultColor = kpi.operatingResult.value >= 0 ? '#52c41a' : '#ff4d4f';

    return (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
            {/* ===== Header: filters + freshness ===== */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'space-between', alignItems: 'center' }}>
                <Space wrap>
                    <Select
                        value={channelType}
                        onChange={(v) => setChannelType(v)}
                        style={{ minWidth: 150 }}
                        allowClear
                        placeholder="Wszystkie kanały"
                        options={[
                            { value: 'ALLEGRO', label: 'Allegro' },
                            { value: 'SHOP', label: 'Sklep' },
                            { value: 'WHOLESALE', label: 'B2B' },
                        ]}
                    />
                    {preset === 'CUSTOM' && (
                        <RangePicker
                            value={customRange}
                            onChange={(dates) => dates && dates[0] && dates[1] && setCustomRange([dates[0], dates[1]])}
                        />
                    )}
                    {isMobile ? (
                        <Select
                            value={preset}
                            onChange={(v) => setPreset(v)}
                            style={{ minWidth: 150 }}
                            options={PRESETS.map((p) => ({ value: p.key, label: p.label }))}
                        />
                    ) : (
                        <Radio.Group
                            value={preset}
                            onChange={(e) => setPreset(e.target.value)}
                            buttonStyle="solid"
                            optionType="button"
                            options={PRESETS.map((p) => ({ value: p.key, label: p.label }))}
                        />
                    )}
                </Space>
                <Tooltip title="Kliknij, aby odświeżyć">
                    <span style={{ fontSize: 12, color: '#999', cursor: 'pointer' }} onClick={() => refetch()}>
                        <ReloadOutlined /> Dane na: {dayjs(dataUpdatedAt).format('HH:mm')}
                    </span>
                </Tooltip>
            </div>

            {/* ===== PIENIĄDZE - KPI row A (identyczne definicje jak w Zamówieniach) ===== */}
            <SectionTitle icon={<ThunderboltOutlined />} title="Wyniki" />
            <Row gutter={[12, 12]}>
                <Col xs={12} sm={6}>
                    <KPICard
                        label="Zamówienia"
                        tooltip={(
                            <div>
                                <strong>Liczba opłaconych zamówień</strong><br />
                                Tylko zamówienia z paymentStatus = PAID<br /><br />
                                <strong>Wyklucza:</strong> oczekujące na płatność, anulowane, zwrócone w całości.
                            </div>
                        )}
                        value={String(kpi.orders.value)}
                        footnote={<ChangeTag change={kpi.orders.change} />}
                    />
                </Col>
                <Col xs={12} sm={6}>
                    <KPICard
                        label="Przychód brutto"
                        tooltip={(
                            <div>
                                Suma opłaconych zamówień minus zwroty.<br />
                                <strong>Wyklucza:</strong> oczekujące, anulowane, nieopłacone.
                            </div>
                        )}
                        value={kpi.revenue.value.toLocaleString('pl-PL', { minimumFractionDigits: 2 })}
                        suffix="PLN"
                        color="#1890ff"
                        footnote={<ChangeTag change={kpi.revenue.change} />}
                    />
                </Col>
                <Col xs={12} sm={6}>
                    <KPICard
                        label="Zysk ze sprzedaży"
                        tooltip={(
                            <div>
                                <strong>Zysk ze sprzedaży</strong> = Przychód brutto<br />
                                − Koszt produktów (COGS)<br />
                                − Opłaty Allegro z bilingu (prowizje, Ads, dostawy, One, inne)<br /><br />
                                Opłaty liczone z pełnego bilingu Allegro po usunięciu duplikatów importu - część opłat
                                nie jest jeszcze przypisana do konkretnych zamówień, dlatego kwota może się różnić
                                od „Zysku netto" w zakładce Zamówienia.
                            </div>
                        )}
                        value={kpi.salesProfit.value.toLocaleString('pl-PL', { minimumFractionDigits: 2 })}
                        suffix="PLN"
                        color={kpi.salesProfit.value >= 0 ? '#52c41a' : '#ff4d4f'}
                        footnote={<ChangeTag change={kpi.salesProfit.change} />}
                    />
                </Col>
                <Col xs={12} sm={6}>
                    <KPICard
                        label="Marża sprzedaży"
                        tooltip="Marża % = (Zysk ze sprzedaży / Przychód brutto) × 100. Liczona tylko dla opłaconych zamówień."
                        value={fmtPct(kpi.margin.value)}
                        color={marginColor}
                        footnote={<ChangeTag change={kpi.margin.change} absolute />}
                    />
                </Col>
            </Row>

            {/* ===== KPI row B: koszty/wynik + Allegro + zwroty (wymaga uprawnienia Koszty) ===== */}
            {canCosts ? (
                <Row gutter={[12, 12]}>
                    <Col xs={12} sm={6}>
                        <KPICard
                            label="Koszty operacyjne firmy"
                            tooltip={(
                                <div>
                                    Ręczne koszty firmowe z zakładki <strong>Koszty</strong>: marketing, logistyka,
                                    biuro, zespół itp.<br />
                                    <strong>Nie zawiera:</strong> kosztu towaru (COGS) ani opłat Allegro - te
                                    są policzone osobno powyżej.
                                </div>
                            )}
                            value={kpi.operatingCosts.value.toLocaleString('pl-PL', { minimumFractionDigits: 2 })}
                            suffix="PLN"
                            color="#fa8c16"
                            footnote={<ChangeTag change={kpi.operatingCosts.change} />}
                        />
                    </Col>
                    <Col xs={12} sm={6}>
                        <KPICard
                            label="Wynik operacyjny"
                            tooltip="= Zysk ze sprzedaży − Koszty operacyjne firmy. Wynik memoriałowy (wynikowo), bez przepływów gotówkowych."
                            value={kpi.operatingResult.value.toLocaleString('pl-PL', { minimumFractionDigits: 2 })}
                            suffix="PLN"
                            color={resultColor}
                            footnote={<ChangeTag change={kpi.operatingResult.change} />}
                        />
                    </Col>
                    <Col xs={12} sm={6}>
                        <KPICard
                            label="Opłaty Allegro"
                            tooltip={(
                                <div>
                                    Prowizje i opłaty z <strong>bilingu Allegro</strong> (po usunięciu duplikatów
                                    importu): prowizje od sprzedaży, Allegro Ads, opłaty za dostawę, One
                                    Fulfillment, płatności, inne.
                                </div>
                            )}
                            value={kpi.allegroFees.value.toLocaleString('pl-PL', { minimumFractionDigits: 2 })}
                            suffix="PLN"
                            color="#ff7875"
                            footnote={<ChangeTag change={kpi.allegroFees.change} />}
                        />
                    </Col>
                    <Col xs={12} sm={6}>
                        <KPICard
                            label="Zwroty (info)"
                            tooltip="Zwrócone kwoty (częściowe zwroty i zamówienia zwrócone w całości) w wybranym okresie."
                            value={`${data.returns.count} zam.`}
                            suffix={data.returns.count > 0 ? fmtPLN(data.returns.amount) : ''}
                            color="#8c8c8c"
                            footnote={
                                data.returns.count > 0
                                    ? `${fmtPct(data.returns.pctOfRevenue)} przychodu`
                                    : 'brak zwrotów w okresie'
                            }
                        />
                    </Col>
                </Row>
            ) : (
                <Alert
                    type="info"
                    showIcon
                    icon={<LockOutlined />}
                    message="Koszty operacyjne, wynik operacyjny i zwroty są ukryte - brak uprawnienia do modułu Koszty."
                />
            )}

            {/* ===== Waterfall + struktura kosztów ===== */}
            {canCosts && (
                <Row gutter={[16, 16]}>
                    <Col xs={24} lg={16}>
                        <Card size="small" title="Skąd się bierze wynik">
                            <ResponsiveContainer width="100%" height={260}>
                                <BarChart data={waterfallChart} margin={{ top: 20, right: 10, bottom: isMobile ? 40 : 5, left: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis
                                        dataKey="name"
                                        fontSize={isMobile ? 8 : 10}
                                        interval={0}
                                        angle={isMobile ? -35 : -15}
                                        textAnchor={isMobile ? 'end' : 'end'}
                                        height={isMobile ? 70 : 50}
                                    />
                                    <YAxis fontSize={isMobile ? 9 : 11} tickFormatter={formatValue} />
                                    <RechartsTooltip
                                        formatter={(value: number) => [`${value.toLocaleString('pl-PL', { minimumFractionDigits: 2 })} PLN`, '']}
                                        labelFormatter={(label: string, payload: any[]) => {
                                            const tt = payload?.[0]?.payload?.tooltip;
                                            return tt || label;
                                        }}
                                    />
                                    <Bar dataKey="base" stackId="w" fill="transparent" />
                                    <Bar dataKey="value" stackId="w" radius={[4, 4, 0, 0]} name="Kwota">
                                        {waterfallChart.map((entry, i) => (
                                            <Cell key={`wf-${i}`} fill={colorFor((entry as any).kind)} />
                                        ))}
                                        <LabelList
                                            dataKey="value"
                                            position="top"
                                            formatter={(v: number) => formatValue(v)}
                                            fontSize={10}
                                        />
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </Card>
                    </Col>
                    <Col xs={24} lg={8}>
                        <Card
                            size="small"
                            title={(
                                <Tooltip title="Wszystkie koszty wybranego okresu (kwoty BRUTTO, zapisane w zakładce Koszty): biling Allegro po grupach opłat + ręczne koszty firmowe. Bez kosztu towaru (COGS) - ten jest na wykresie po lewej.">
                                    <span>Struktura kosztów (z bilingiem Allegro) <QuestionCircleOutlined style={{ fontSize: 10 }} /></span>
                                </Tooltip>
                            )}
                        >
                            {data.costStructure.length > 0 ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                    {data.costStructure.map((item) => (
                                        <div key={item.category}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 2 }}>
                                                <span>{item.category}</span>
                                                <span style={{ color: '#666' }}>
                                                    {fmtPLN(item.amount)} · {fmtPct(item.percentage)}
                                                </span>
                                            </div>
                                            <Progress percent={item.percentage} showInfo={false} size="small" strokeColor="#fa8c16" />
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <Empty description="Brak kosztów w tym okresie" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                            )}
                        </Card>
                    </Col>
                </Row>
            )}

            {/* ===== SPRZEDAŻ (identyczne wykresy jak w Zamówieniach) ===== */}
            <SectionTitle icon={<LineChartOutlined />} title="Sprzedaż" />
            <Row gutter={[16, 16]}>
                <Col xs={24} lg={16}>
                    <Card size="small" title="Sprzedaż dzienna">
                        {data.sales.daily && data.sales.daily.length > 0 ? (
                            <ResponsiveContainer width="100%" height={200}>
                                <LineChart
                                    data={data.sales.daily.map((d) => ({ ...d, date: dayjs(d.date).format('DD.MM') }))}
                                    margin={{ top: 5, right: 20, bottom: 5, left: 0 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="date" fontSize={isMobile ? 9 : 11} />
                                    <YAxis fontSize={isMobile ? 9 : 11} tickFormatter={formatValue} />
                                    <RechartsTooltip
                                        formatter={(value: number) => [`${(Number(value) || 0).toLocaleString('pl-PL', { minimumFractionDigits: 2 })} PLN`, 'Przychód']}
                                    />
                                    <Line type="monotone" dataKey="revenue" stroke="#1890ff" name="Przychód" strokeWidth={2} dot={{ r: 2 }} activeDot={{ r: 5 }} />
                                </LineChart>
                            </ResponsiveContainer>
                        ) : (
                            <div style={{ textAlign: 'center', padding: isMobile ? 20 : 40, color: '#999' }}>Brak danych</div>
                        )}
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={8}>
                    <Card
                        size="small"
                        title="Trend sprzedaży"
                        style={{ height: '100%' }}
                        styles={{ body: { display: 'flex', flexDirection: 'column', justifyContent: 'center', height: 'calc(100% - 48px)' } }}
                    >
                        {data.sales.trend ? (
                            <div style={{ textAlign: 'center' }}>
                                <div style={{
                                    fontSize: isMobile ? 48 : 64, lineHeight: 1, marginBottom: 8,
                                    color: data.sales.trend.change > 5 ? '#52c41a' : data.sales.trend.change < -5 ? '#ff4d4f' : '#faad14',
                                }}>
                                    {data.sales.trend.change > 5 ? '↑' : data.sales.trend.change < -5 ? '↓' : '→'}
                                </div>
                                <div style={{
                                    fontSize: isMobile ? 24 : 32, fontWeight: 700,
                                    color: data.sales.trend.change > 0 ? '#52c41a' : data.sales.trend.change < 0 ? '#ff4d4f' : '#666',
                                }}>
                                    {data.sales.trend.change > 0 ? '+' : ''}{data.sales.trend.change.toFixed(1)}%
                                </div>
                                <div style={{ color: '#999', fontSize: 12, marginTop: 4, marginBottom: 16 }}>vs poprzedni okres</div>
                                <Row gutter={8} style={{ background: token.colorFillAlter, borderRadius: 8, padding: '12px 8px', margin: '0 -8px' }}>
                                    <Col span={12} style={{ textAlign: 'center', borderRight: `1px solid ${token.colorBorderSecondary}` }}>
                                        <Tooltip title="Średni dzienny przychód z wybranego okresu">
                                            <div style={{ fontSize: 11, color: token.colorTextDescription, cursor: 'help' }}>Obecny okres</div>
                                        </Tooltip>
                                        <div style={{ fontSize: isMobile ? 12 : 14, fontWeight: 600 }}>{fmtPLN(data.sales.trend.avgCurrent, 0)}</div>
                                    </Col>
                                    <Col span={12} style={{ textAlign: 'center' }}>
                                        <Tooltip title="Średni dzienny przychód z poprzedniego równoważnego okresu">
                                            <div style={{ fontSize: 11, color: token.colorTextDescription, cursor: 'help' }}>Poprz. okres</div>
                                        </Tooltip>
                                        <div style={{ fontSize: isMobile ? 12 : 14, fontWeight: 600 }}>{fmtPLN(data.sales.trend.avgPrev, 0)}</div>
                                    </Col>
                                </Row>
                            </div>
                        ) : (
                            <div style={{ textAlign: 'center', padding: isMobile ? 20 : 40, color: '#999' }}>Brak danych</div>
                        )}
                    </Card>
                </Col>
                <Col xs={24} lg={16}>
                    <Card size="small" title="Udział produktów w przychodzie (Top 10)">
                        {data.sales.products && data.sales.products.length > 0 ? (
                            <ResponsiveContainer width="100%" height={280}>
                                <BarChart data={data.sales.products} layout="vertical" margin={{ left: isMobile ? 60 : 100, right: 20 }}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis type="number" fontSize={isMobile ? 9 : 11} tickFormatter={formatValue} />
                                    <YAxis type="category" dataKey="name" fontSize={isMobile ? 8 : 10} width={isMobile ? 55 : 95} tick={{ fill: '#333' }} />
                                    <RechartsTooltip
                                        formatter={(value: number) => [`${(Number(value) || 0).toLocaleString('pl-PL', { minimumFractionDigits: 2 })} PLN`, 'Przychód']}
                                    />
                                    <Bar dataKey="value" fill="#1890ff" name="Przychód" radius={[0, 4, 4, 0]}>
                                        {data.sales.products.map((_: any, index: number) => (
                                            <Cell key={`cell-${index}`} fill={['#1890ff', '#52c41a', '#722ed1', '#faad14', '#13c2c2', '#eb2f96', '#fa8c16', '#2f54eb', '#a0d911', '#ff4d4f'][index % 10]} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        ) : (
                            <div style={{ textAlign: 'center', padding: isMobile ? 20 : 40, color: '#999' }}>
                                <BarChartOutlined style={{ fontSize: 32, marginBottom: 8 }} />
                                <div>Brak danych o produktach</div>
                            </div>
                        )}
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={8}>
                    <Card
                        size="small"
                        title={(
                            <Tooltip title="Podział przychodu brutto wybranego okresu na kanały sprzedaży. Marża kanału = zysk na zamówieniach kanału / przychód kanału.">
                                <span>Miks kanałów <QuestionCircleOutlined style={{ fontSize: 10 }} /></span>
                            </Tooltip>
                        )}
                    >
                        {data.channels.length > 0 ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                {data.channels.map((ch) => (
                                    <div key={ch.label}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 2 }}>
                                            <span style={{ fontWeight: 600 }}>{ch.label}</span>
                                            <span>{fmtPct(ch.share)} przychodu</span>
                                        </div>
                                        <Progress percent={ch.share} showInfo={false} size="small" strokeColor="#1890ff" />
                                        <div style={{ fontSize: 11, color: '#666' }}>
                                            {ch.orders} zam. · {fmtPLN(ch.revenue, 0)} · marża {fmtPct(ch.margin)}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <Empty description="Brak danych" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                        )}
                    </Card>
                </Col>
            </Row>

            {/* ===== MAGAZYN I OPERACJE (wymaga uprawnienia Magazyn) ===== */}
            {canInventory && (
                <>
                    <SectionTitle
                        icon={<InboxOutlined />}
                        title="Magazyn i operacje"
                        extra={<LockedTag />}
                    />
                    <Row gutter={[12, 12]}>
                        <Col xs={12} sm={6} style={{ display: 'flex' }}>
                            <Card size="small" style={{ height: '100%', width: '100%' }}>
                                <Statistic
                                    title={<Tooltip title="Opłacone zamówienia oczekujące na wysyłkę (potwierdzone, w realizacji, spakowane). Niezależne od filtra daty.">Do wysyłki</Tooltip>}
                                    value={data.operations.toShip}
                                    valueStyle={{ color: data.operations.toShip > 0 ? '#faad14' : '#52c41a' }}
                                />
                            </Card>
                        </Col>
                        <Col xs={12} sm={6} style={{ display: 'flex' }}>
                            <Card size="small" style={{ height: '100%', width: '100%' }}>
                                <Statistic
                                    title={<Tooltip title="Opłacone zamówienia czekające na wysyłkę dłużej niż 48 h (licząc od daty zamówienia/opłacenia). Niezależne od filtra daty.">Przekroczone SLA</Tooltip>}
                                    value={data.operations.slaBreaches}
                                    valueStyle={{ color: data.operations.slaBreaches > 0 ? '#ff4d4f' : '#52c41a' }}
                                />
                                {data.operations.oldestUnshippedHours != null && data.operations.slaBreaches > 0 && (
                                    <div style={{ fontSize: 11, color: '#999', marginTop: -6 }}>
                                        najstarsze: {fmtDuration(data.operations.oldestUnshippedHours)}
                                    </div>
                                )}
                            </Card>
                        </Col>
                        {data.warehouse.locations.map((loc) => (
                            <Col xs={12} sm={6} key={loc.name} style={{ display: 'flex' }}>
                                <Card size="small" style={{ height: '100%', width: '100%' }}>
                                    <Statistic
                                        title={(
                                            <Tooltip title={`Stan z księgi magazynowej (bilans zdarzeń) dla produktów aktywnych. W tym magazynie: ${loc.products} produkt(y).`}>
                                                <span style={{ display: 'inline-block', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {loc.name}
                                                </span>
                                            </Tooltip>
                                        )}
                                        value={loc.units}
                                        suffix="szt."
                                    />
                                </Card>
                            </Col>
                        ))}
                    </Row>

                    {/* Które produkty się kończą - 5 najniższych łącznych stanów */}
                    {data.warehouse.lowestStock && data.warehouse.lowestStock.length > 0 && (
                        <Card
                            size="small"
                            title={(
                                <Tooltip title="5 produktów aktywnych o NAJNIŻSZYM łącznym stanie ze wszystkich magazynów (bilans księgi magazynowej). Kolor słupka: czerwony = stan poniżej minimum / brak, pomarańczowy = blisko minimum, niebieski = OK. Najedź na produkt, aby zobaczyć podział na magazyny.">
                                    <span>Które produkty się kończą? (Top 5 najniższych stanów) <QuestionCircleOutlined style={{ fontSize: 10 }} /></span>
                                </Tooltip>
                            )}
                        >
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                {(() => {
                                    const items = data.warehouse.lowestStock;
                                    const maxUnits = Math.max(1, ...items.map((i) => i.units));
                                    return items.map((p) => {
                                        const min = p.minStockLevel;
                                        let color = '#1890ff';
                                        if (min && min > 0) {
                                            color = p.units < min ? '#ff4d4f' : p.units <= min * 1.5 ? '#faad14' : '#1890ff';
                                        } else if (p.units <= 0) {
                                            color = '#ff4d4f';
                                        } else if (p.units <= 10) {
                                            color = '#faad14';
                                        }
                                        const splitText = p.split.length > 0
                                            ? p.split.map((s) => `${s.name}: ${s.units} szt.`).join(' · ')
                                            : 'brak stanu we wszystkich magazynach';
                                        return (
                                            <div key={p.id} style={{ minWidth: 0 }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 12, marginBottom: 2 }}>
                                                    <Tooltip
                                                        title={(
                                                            <div>
                                                                <strong>{p.name}</strong>
                                                                {p.sku ? <div>SKU: {p.sku}</div> : null}
                                                                <div style={{ marginTop: 4 }}>{splitText}</div>
                                                                {min && min > 0 ? <div style={{ marginTop: 4 }}>Stan minimalny: {min} szt.</div> : null}
                                                            </div>
                                                        )}
                                                    >
                                                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'help' }}>
                                                            {p.name}
                                                        </span>
                                                    </Tooltip>
                                                    <span style={{ whiteSpace: 'nowrap', color: color === '#1890ff' ? '#666' : color, fontWeight: 600 }}>
                                                        {p.units} szt.
                                                    </span>
                                                </div>
                                                <Progress
                                                    percent={Math.max(3, Math.round((p.units / maxUnits) * 100))}
                                                    showInfo={false}
                                                    size="small"
                                                    strokeColor={color}
                                                    style={{ marginBottom: 0 }}
                                                />
                                            </div>
                                        );
                                    });
                                })()}
                            </div>
                        </Card>
                    )}
                </>
            )}

            {/* ===== OPINIE - kontakt z klientami (wymaga uprawnienia CRM) ===== */}
            {canPartners && (
                <>
                    <SectionTitle
                        icon={<PhoneOutlined />}
                        title="Opinie - kontakt z klientami"
                        extra={(
                            <Tooltip title="Te same definicje co w zakładce Opinie (B2C). Nie pokazujemy liczby wystawionych opinii, bo ich nie monitorujemy.">
                                <InfoCircleOutlined style={{ color: '#999' }} />
                            </Tooltip>
                        )}
                    />
                    <Row gutter={[12, 12]}>
                        <Col xs={24} sm={6} style={{ display: 'flex' }}>
                            <Card size="small" style={{ height: '100%', width: '100%' }}>
                                <Statistic
                                    title={<Tooltip title="Klienci z dostarczonym zamówieniem, do których jeszcze nikt nie zadzwonił (status: Do kontaktu w zakładce Opinie). Aktualna zaległość, niezależna od filtra daty.">Do kontaktu</Tooltip>}
                                    value={data.reviews.pending}
                                    valueStyle={{ color: data.reviews.pending > 0 ? '#faad14' : '#52c41a' }}
                                />
                            </Card>
                        </Col>
                        <Col xs={24} sm={18} style={{ display: 'flex' }}>
                            <Card
                                size="small"
                                style={{ height: '100%', width: '100%' }}
                                title={(
                                    <Tooltip title="Kontakty z klientami zakończone danego dnia (dowolny status kontaktu), wg daty kontaktu - identycznie jak w zakładce Opinie. Tydzień liczy się od poniedziałku, miesiąc od 1. dnia miesiąca.">
                                        <span>Skontaktowane <QuestionCircleOutlined style={{ fontSize: 10 }} /></span>
                                    </Tooltip>
                                )}
                            >
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(90px, 1fr))' }}>
                                    {([
                                        { label: 'Dziś', value: data.reviews.contactedToday, tip: 'Kontakty zakończone dzisiaj (od północy).' },
                                        { label: 'Wczoraj', value: data.reviews.contactedYesterday, tip: 'Kontakty zakończone wczoraj.' },
                                        { label: 'Ten tydzień', value: data.reviews.contactedThisWeek, tip: 'Kontakty zakończone od poniedziałku tego tygodnia (włącznie z dzisiejszymi).' },
                                        { label: 'Ten miesiąc', value: data.reviews.contactedThisMonth, tip: 'Kontakty zakończone od 1. dnia bieżącego miesiąca (włącznie z dzisiejszymi).' },
                                    ] as const).map((item, i, arr) => (
                                        <Tooltip key={item.label} title={item.tip}>
                                            <div
                                                style={{
                                                    textAlign: 'center',
                                                    padding: '6px 8px',
                                                    cursor: 'help',
                                                    borderRight: i < arr.length - 1 ? `1px solid ${token.colorBorderSecondary}` : undefined,
                                                }}
                                            >
                                                <div style={{ fontSize: 12, color: token.colorTextSecondary }}>{item.label}</div>
                                                <div style={{ fontSize: 22, fontWeight: 600, lineHeight: '30px', color: item.value > 0 ? token.colorSuccess : token.colorText }}>
                                                    {item.value}
                                                </div>
                                            </div>
                                        </Tooltip>
                                    ))}
                                </div>
                            </Card>
                        </Col>
                    </Row>
                </>
            )}

            {/* ===== KANAŁ B2B (wymaga uprawnienia CRM / partners) ===== */}
            {canPartners && data.b2b && data.b2b.contractors.length > 0 && (
                <>
                    <SectionTitle icon={<BankOutlined />} title="Kanał B2B" />
                    <Card size="small">
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {data.b2b.contractors.map((c) => (
                                <div
                                    key={c.name}
                                    style={{
                                        display: 'flex', flexWrap: 'wrap', gap: isMobile ? 4 : 16, alignItems: 'center',
                                        padding: '6px 10px', background: token.colorFillAlter, borderRadius: 8,
                                    }}
                                >
                                    <BankOutlined style={{ color: '#1890ff' }} />
                                    <span style={{ fontWeight: 600 }}>{c.name}</span>
                                    <span style={{ fontSize: 12, color: '#666' }}>
                                        {c.orders} zam. · {fmtPLN(c.revenue)} · marża {fmtPct(c.margin)}
                                    </span>
                                    <a href="/crm/partners" style={{ marginLeft: 'auto', fontSize: 12 }}>Karta kontrahenta →</a>
                                </div>
                            ))}
                        </div>
                    </Card>
                </>
            )}

            {/* ===== Stopka informacyjna ===== */}
            <div style={{ fontSize: 11, color: '#999', paddingBottom: 8 }}>
                Wszystkie kwoty BRUTTO (PLN), podejście wynikowe (memoriał) - bez przepływów gotówkowych.
                Źródła danych: Zamówienia (sprzedaż), Koszty (koszty firmowe i biling Allegro), Magazyn (stany, FEFO), B2B/CRM (kontrahenci).
                Kliknij ⓘ przy dowolnej karcie, aby zobaczyć wzór.
            </div>
        </Space>
    );
};

export default CompanyDashboard;
